import { DefaultHandlers, TypedPathWrapper } from "typed-path";
import React, { Dispatch, SetStateAction, useMemo, useState } from "react";

export interface FormikWizardStep<TFormState, TStepProps> {
  // i18next key for the title of the step (this allows the keys to be recognized
  // as string keys by lintlocalization)
  titleI18nKey: string;
  // an array of typedPath objects which point into the form state and can be
  // used for partially validating the visible form state with e.g. `validateThen`
  editedFields: TypedPathWrapper<unknown, DefaultHandlers>[];
  // a function which returns true if the next button should be enabled; if
  // canProceed is undefined, the next button is always enabled
  canProceed?: (formState: TFormState) => boolean;
  // a React component which renders the body of the wizard
  RenderBody: React.FC<TStepProps>;
}

interface FormikWizard<TFormState, TStepProps> {
  // the index of the active step in steps
  activeStepIndex: number;
  // a setState hook that changes the index of the active step
  setActiveStepIndex: Dispatch<SetStateAction<number>>;
  // the list of steps passed in
  steps: FormikWizardStep<TFormState, TStepProps>[];
  // the active step's description
  activeStep: FormikWizardStep<TFormState, TStepProps> | undefined;
  // a React component which renders the active step
  StepRenderer: React.FC<TStepProps>;
}

/**
 * Hook to help build wizard-style multi-page stepper-driven UIs with Formik
 * declaratively.
 * @param steps an array of step descriptions
 * @returns a variety of helpful wizard pieces
 */
export function useFormikWizard<TFormState, TStepProps>(
  steps: FormikWizardStep<TFormState, TStepProps>[]
): FormikWizard<TFormState, TStepProps> {
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
  const activeStep = useMemo(
    () => steps[activeStepIndex],
    [activeStepIndex, steps]
  );

  const StepRenderer = (props: TStepProps): JSX.Element => (
    <>
      {steps.map(
        (step, i) =>
          activeStepIndex === i && (
            <step.RenderBody key={step.titleI18nKey} {...props} />
          )
      )}
    </>
  );

  return {
    activeStepIndex,
    setActiveStepIndex,
    activeStep,
    steps,
    StepRenderer,
  };
}
